import React, {useContext, useEffect, useRef, useState, useLayoutEffect} from "react";
import { Media, Button, Badge } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { formatDistanceToNow, parseISO } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useSubscription, useMutation } from "@apollo/client";
import * as Sentry from "@sentry/react";

import getEncodedImageUrl from "../../helpers/get-encoded-image";
import UserContext from "../../contexts/user";
import { PreLoader } from "../preloader";
import Reply from "./reply";
import {
  DELETE_POST,
  SUBSCRIBE_POSTS,
  INSERT_POST_LIKE_ONE,
  DELETE_POST_LIKE_BY_PK,
  INSERT_POST_REPLY_ONE,
  DELETE_POST_REPLY,
  INSERT_POST_REPLY_LIKE_ONE,
  DELETE_POST_REPLY_LIKE_BY_PK,
} from "../../gql/forum";
import { DateTime } from "luxon";

const Comments: React.FC<{ presentation_id: string }> = ({
  presentation_id,
}) => {
  const intl = useIntl();
  const userContext = useContext(UserContext);

  const [deletingPost, setDeletingPost] = useState("");
  const [deletingPostReply, setDeletingPostReply] = useState("");
  const [firstScrollLoad, setFirstScrollLoad] = useState(true);

  const messageEl = useRef<any>();


  useEffect(() => {
    if (messageEl && messageEl.current) {

      messageEl.current.scroll({ top: 0, behavior: 'smooth' });
      messageEl.current.addEventListener('DOMNodeInserted',function (event:any) {
        const { currentTarget: target } = event;
        target.scroll({ top: target.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [messageEl.current])
  const { loading, data, error } = useSubscription(SUBSCRIBE_POSTS, {
    variables: {
      id: presentation_id,
    },
    skip: !presentation_id,
  });

  const [deletePost] = useMutation(DELETE_POST, {
    onError: (e) => {
      // TODO
      //Sentry.captureException(e);
      alert("There was an error while deleting the post");
    },
    onCompleted: (resp) => {
      if (!resp.delete_post_by_pk) {
        // TODO
        alert("There was an error while deleting the post");
      }
    },
  });

  const [likePost] = useMutation(INSERT_POST_LIKE_ONE, {
    onError: (e) => {
      //Sentry.captureException(e);
      alert("There was an error while liking the post");
    },
  });

  const [unlikePost] = useMutation(DELETE_POST_LIKE_BY_PK, {
    onError: (e) => {
      //Sentry.captureException(e);

      alert("There was an error while unliking the post");
    },
  });

  const [postReply] = useMutation(INSERT_POST_REPLY_ONE, {
    onError: (e) => {
      //Sentry.captureException(e);
      // TODO
      alert("There was an error while replying");
    },
  });

  const [deleteReply] = useMutation(DELETE_POST_REPLY, {
    onError: (e) => {
      //Sentry.captureException(e);
      // TODO
      alert("There was an error while deleting your reply");
    },
    onCompleted: (resp) => {
      if (!resp.delete_post_reply_by_pk) {
        // TODO
        alert("There was an error while deleting your reply");
      }
    },
  });

  const [likePostReply] = useMutation(INSERT_POST_REPLY_LIKE_ONE, {
    onError: (e) => {
      //Sentry.captureException(e);
      alert("There was an error while liking the reply");
    },
  });

  const [unlikePostReply] = useMutation(DELETE_POST_REPLY_LIKE_BY_PK, {
    onError: (e) => {
      //Sentry.captureException(e);
      // TODO
      alert("There was an error while unliking the reply");
    },
  });

  useEffect(() => {
    if (error) {
      //Sentry.captureException(error);
    }
  }, [error]);

  if (loading) {
    return <PreLoader />;
  }

  if (error) {
    return <pre>{JSON.stringify(error, null, 2)}</pre>;
    // return <i>There was an error</i>
  }

  // return <pre>{JSON.stringify(data, null, 2)}</pre>

  const { posts, speaker_id, association_id } = data?.presentation_by_pk;
   console.log("posts---------------------------------------------------",posts)

  if (posts?.length === 0) {
    return (
      <div className="no-post-comments">
        {intl.formatMessage({ id: "no.post.comments" })}
      </div>
    );
  }

  return (
    <div className="comments-container">
      <div className="m-comment m-b-20"  style={{ overflow: "auto"}} ref={messageEl}>
        {posts
          .sort((a: any, b: any) => {
            return (
              DateTime.fromISO(a.created_at).toMillis() -
              DateTime.fromISO(b.created_at).toMillis()

            );
          })
          .map((post: any) => (
            <React.Fragment key={post.id}>
              <ForumPost
                {...post}
                speaker_id={speaker_id}
                association_id={association_id}
              />
              <div className="post-details">
                <small>
                  {formatDistanceToNow(parseISO(post.created_at), {
                    addSuffix: true,
                    locale: intl.locale === "fr" ? fr : enUS,
                  })}
                  &nbsp; |&nbsp;
                </small>
                <small>
                  {post.likes} <FormattedMessage id="forum.comment.likes" /> &nbsp;
                </small>

                {/* <pre style={{ color: '#fff' }}>
                {JSON.stringify(post.debug, null, 2)}
              </pre> */}

                {post.liked_by_user ? (
                  <Button
                    color="link"
                    size="xs"
                    onClick={() =>
                      unlikePost({ variables: { post_id: post.id } })
                    }
                    // disabled={unlikePostLoading}
                  >
                    <i className="fas fa-thumbs-down"></i>{" "}
                    <FormattedMessage id="forum.comment.unlike" />
                  </Button>
                ) : (
                  <Button
                    color="link"
                    size="xs"
                    onClick={() =>
                      likePost({ variables: { post_id: post.id } })
                    }
                    // disabled={likePostLoading}
                  >
                    <i className="fas fa-thumbs-up"></i>{" "}
                    <FormattedMessage id="forum.comment.like" />
                  </Button>
                )}

                <Button color="link" size="xs" id={`reply-${post.id}`}>
                  <i className="far fa-comment"></i>{" "}
                  <FormattedMessage id="forum.comment.reply" />
                </Button>

                {(userContext.user?.id === post.user?.id ||
                  userContext.user?.is_moderator) && (
                  <Button
                    disabled={post.id === deletingPost}
                    color="link"
                    size="xs"
                    onClick={async () => {
                      setDeletingPost(post.id);
                      await deletePost({
                        variables: {
                          id: post.id,
                        },
                      });
                      setDeletingPost("");
                    }}
                  >
                    <i className="fas fa-trash-alt"></i>{" "}
                    <FormattedMessage id="forum.comment.delete" />
                  </Button>
                )}
              </div>

              {post.replies
                .sort((a: any, b: any) => {
                  return (
                    DateTime.fromISO(a.created_at).toMillis() -
                    DateTime.fromISO(b.created_at).toMillis()
                  );
                })
                .map((r: any) => (
                  <div className="m-comment comment-reply m-l-80 m-b-20">
                    <Media>
                      <Media left href="#">
                        <Media
                          object
                          src={
                            r.user?.photo
                              ? getEncodedImageUrl(r.user?.photo, 48, 48)
                              : `https://ui-avatars.com/api/?name=${r.user?.name}&size=48&rounded=false&font-size=0.4`
                          }
                          alt="Generic placeholder image"
                        />
                      </Media>
                      <Media body>
                        <Media heading>
                          {r.user?.name}
                          {r.user?.moderator && (
                            <Badge color="primary">Moderator</Badge>
                          )}
                          {speaker_id === r.user?.external_id && (
                            <Badge color="primary">Speaker</Badge>
                          )}
                          {association_id === r.user?.external_id && (
                            <Badge color="primary">Association</Badge>
                          )}
                        </Media>
                        {r.reply}
                      </Media>
                    </Media>
                    <div className="post-details">
                      <small>
                        {formatDistanceToNow(parseISO(r.created_at), {
                          addSuffix: true,
                          locale: intl.locale === "fr" ? fr : enUS,
                        })}
                        &nbsp;|&nbsp;
                      </small>
                      <small>
                        {r.likes} <FormattedMessage id="forum.comment.likes" />
                        &nbsp;
                      </small>
                      {(userContext.user?.id === r.user?.id ||
                        userContext.user?.is_moderator) && (
                        <Button
                          disabled={r.id === deletingPostReply}
                          color="link"
                          size="xs"
                          onClick={async () => {
                            setDeletingPostReply(r.id);
                            await deleteReply({
                              variables: {
                                id: r.id,
                              },
                            });
                            setDeletingPostReply("");
                          }}
                        >
                          <i className="fas fa-trash-alt"></i>{" "}
                          <FormattedMessage id="forum.comment.delete" />
                        </Button>
                      )}
                      {r.liked_by_user ? (
                        <Button
                          color="link"
                          size="xs"
                          onClick={() =>
                            unlikePostReply({
                              variables: { id: r.post_reply_likes[0].id },
                            })
                          }
                          // disabled={unlikePostLoading}
                        >
                          <i className="fas fa-thumbs-down"></i>{" "}
                          <FormattedMessage id="forum.comment.unlike" />
                        </Button>
                      ) : (
                        <Button
                          color="link"
                          size="xs"
                          onClick={() =>
                            likePostReply({
                              variables: { post_reply_id: r.id },
                            })
                          }
                          // disabled={likePostLoading}
                        >
                          <i className="fas fa-thumbs-up"></i>{" "}
                          <FormattedMessage id="forum.comment.like" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              <Reply postId={post.id} submit={postReply} />
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};

const ForumPost: React.FC<{
  user: any;
  post: any;
  speaker_id: string;
  association_id: string;
}> = ({ user, post, speaker_id, association_id }) => {
  return (
    <Media>
      <Media left href="#">
        <Media
          object
          src={
            user?.photo
              ? getEncodedImageUrl(user.photo, 48, 48)
              : `https://ui-avatars.com/api/?name=${user?.name}&size=48&rounded=false&font-size=0.4`
          }
          alt={`${user?.name}`}
        />
      </Media>
      <Media body>
        <Media heading>
          {user?.name}
          {user?.moderator && <Badge color="primary">Moderator</Badge>}
          {speaker_id === user?.external_id && (
            <Badge color="primary">Speaker</Badge>
          )}
          {association_id === user?.external_id && (
            <Badge color="primary">Association</Badge>
          )}
        </Media>
        {post}
      </Media>
    </Media>
  );
};

export default Comments;
